<script lang="ts">
  import { ArrowLeft, Download, FolderOpen, FolderPlus, Pencil, UploadCloud, X } from 'lucide-svelte';
  import {
    assignFileRoom,
    confirmUpload,
    createFolder,
    deleteFile,
    deleteFolder,
    getDownloadURL,
    getErrorMessage,
    getUploadUrl,
    moveFile,
    renameFolder
  } from '$lib/api';
  import { roomsQuery } from '$lib/queries/rooms';
  import { projectFilesQuery } from '$lib/queries/project_files';
  import { projectFileFoldersQuery } from '$lib/queries/project_file_folders';
  import {
    invalidateAfterProjectFileFoldersUpdate,
    invalidateAfterProjectFilesUpdate
  } from '$lib/state/invalidation';
  import { showError, showSuccess } from '$lib/state/toast.svelte';
  import { downloadFolderZip } from '$lib/utils/download';
  import ConfirmDialog from './ui/ConfirmDialog.svelte';
  import type { ProjectFile, ProjectFileFolder } from '$lib/types/project_file';

  // Drag-and-drop + click-to-browse file manager, shared by the project-level
  // Files tab (unitId omitted -> all project files) and the unit-level Files
  // section (unitId set -> that unit's own files). A roomId shows that room's
  // uploads as a flat list, including files stored in unit folders. Folders are flat (no
  // nesting): every file either belongs to one folder or sits at the root.
  // Click a folder to navigate into it (full-size drop zone + its files).
  // The root view shows the main drop zone + folder list + root files.
  let {
    projectId,
    unitId,
    roomId,
    emptyLabel = 'No files uploaded'
  }: {
    projectId: number;
    unitId?: number;
    roomId?: number;
    emptyLabel?: string;
  } = $props();

  const filesQ = projectFilesQuery(() => projectId, () => unitId, () => roomId);
  const roomsQ = roomsQuery(() => unitId ?? 0);
  let files = $derived((filesQ.data ?? []) as ProjectFile[]);
  const foldersQ = projectFileFoldersQuery(() => projectId);
  let folders = $derived((foldersQ.data ?? []) as ProjectFileFolder[]);
  let rootFiles = $derived(files.filter((f) => roomId != null || f.folder_id == null));
  const filesInFolder = (folderId: number) => files.filter((f) => f.folder_id === folderId);

  let dragging = $state(false);
  let uploading = $state(false);
  let uploadError = $state('');
  let fileInputEl: HTMLInputElement | undefined = $state();
  let showDeleteConfirm = $state(false);
  let fileToDelete = $state<ProjectFile | null>(null);
  let showDeleteFolderConfirm = $state(false);
  let folderToDelete = $state<ProjectFileFolder | null>(null);
  let showNewFolderInput = $state(false);
  let newFolderName = $state('');
  let renamingFolderId = $state<number | null>(null);
  let renameValue = $state('');
  let activeFolderId = $state<number | null>(null);
  let folderDragging = $state<number | null>(null);

  const s3UnconfiguredMessage = 'File storage not configured (AWS_* env vars).';

  let activeFolder = $derived(
    activeFolderId != null
      ? folders.find((f) => f.id === activeFolderId) ?? null
      : null
  );

  $effect(() => {
    if (activeFolderId != null && folders.length > 0 && !activeFolder) {
      activeFolderId = null;
    }
  });

  // --- Directory drag-and-drop helpers ---

  async function filesFromDragEvent(e: DragEvent): Promise<File[]> {
    const items = e.dataTransfer?.items;
    if (items && items.length > 0) {
      const entries: FileSystemEntry[] = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry();
        if (entry) entries.push(entry);
      }
      if (entries.length > 0) {
        const files: File[] = [];
        for (const entry of entries) {
          await collectFromEntry(entry, files);
        }
        return files;
      }
    }
    return Array.from(e.dataTransfer?.files ?? []);
  }

  async function collectFromEntry(entry: FileSystemEntry, files: File[]) {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) => {
        (entry as FileSystemFileEntry).file(resolve, reject);
      });
      files.push(file);
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      const subEntries = await new Promise<FileSystemEntry[]>((resolve) => {
        reader.readEntries(resolve);
      });
      for (const sub of subEntries) {
        await collectFromEntry(sub, files);
      }
    }
  }

  function topLevelDirName(fileList: FileList | File[]): string | null {
    for (let i = 0; i < fileList.length; i++) {
      const path = fileList[i].webkitRelativePath;
      if (path) return path.split('/')[0];
    }
    return null;
  }

  function navigateToFolder(folderId: number) {
    activeFolderId = folderId;
  }

  function navigateToRoot() {
    activeFolderId = null;
  }

  function refreshFiles() {
    invalidateAfterProjectFilesUpdate(projectId);
  }

  function refreshFolders() {
    invalidateAfterProjectFileFoldersUpdate(projectId);
  }

  async function uploadFiles(fileList: FileList | File[], folderId?: number | null) {
    if (!projectId) return;
    uploadError = '';
    uploading = true;
    const uploadProjectId = projectId;
    const uploadUnitId = unitId;
    const uploadRoomId = roomId;
    try {
      for (const file of Array.from(fileList)) {
        const contentType = file.type || 'application/octet-stream';
        const meta = await getUploadUrl({
          project: uploadProjectId,
          unit: uploadUnitId,
          room: uploadRoomId,
          filename: file.name,
          content_type: contentType,
          size: file.size
        });
        const putResponse = await fetch(meta.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': contentType },
          body: file
        });
        if (!putResponse.ok) {
          throw new Error(`Unable to upload ${file.name}: ${putResponse.statusText}`);
        }
        await confirmUpload({
          project: uploadProjectId,
          unit: uploadUnitId,
          room: uploadRoomId,
          folder_id: folderId ?? undefined,
          filename: file.name,
          s3_key: meta.s3_key,
          size: file.size,
          content_type: contentType
        });
      }
      invalidateAfterProjectFilesUpdate(uploadProjectId);
      showSuccess('File(s) uploaded successfully.');
    } catch (e) {
      const message = getErrorMessage(e);
      uploadError = message.includes('S3 is not configured')
        ? s3UnconfiguredMessage
        : `Upload failed: ${message}`;
    } finally {
      uploading = false;
    }
  }

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    dragging = true;
  }

  function handleDragLeave() {
    dragging = false;
  }

  async function handleDrop(e: DragEvent) {
    e.preventDefault();
    dragging = false;
    const files = await filesFromDragEvent(e);
    if (files.length) uploadFiles(files, activeFolderId);
  }

  function handleDirButtonClick() {
    if (fileInputEl) {
      (fileInputEl as any).webkitdirectory = true;
      fileInputEl.click();
    }
  }

  async function handleFileInputChange(e: Event) {
    const input = e.target as HTMLInputElement;
    const files = input.files;
    if (!files || !files.length) {
      input.value = '';
      (input as any).webkitdirectory = false;
      return;
    }

    let folderId: number | undefined;

    if (activeFolderId != null) {
      folderId = activeFolderId;
    } else {
      const dirName = topLevelDirName(files);
      if (dirName && roomId == null) {
        const existing = folders.find((f) => f.name === dirName);
        if (existing) {
          folderId = existing.id;
        } else {
          try {
            const folder = await createFolder(projectId, dirName);
            refreshFolders();
            folderId = folder.id;
          } catch (e) {
            showError('Could not create folder: ' + getErrorMessage(e));
          }
        }
      }
    }

    await uploadFiles(files, folderId);
    input.value = '';
    (input as any).webkitdirectory = false;
  }

  async function openFile(file: ProjectFile) {
    try {
      const { download_url } = await getDownloadURL(file.id);
      window.open(download_url, '_blank');
    } catch (e) {
      uploadError = 'Could not open file: ' + getErrorMessage(e);
    }
  }

  function confirmDeleteFile(file: ProjectFile) {
    fileToDelete = file;
    showDeleteConfirm = true;
  }

  async function handleDeleteFile() {
    showDeleteConfirm = false;
    if (!fileToDelete) return;
    try {
      await deleteFile(fileToDelete.id);
      showSuccess('File deleted successfully.');
      refreshFiles();
    } catch (e) {
      showError('Delete failed: ' + getErrorMessage(e));
    } finally {
      fileToDelete = null;
    }
  }

  async function handleAssignRoom(file: ProjectFile, e: Event) {
    const value = (e.currentTarget as HTMLSelectElement).value;
    try {
      await assignFileRoom(file.id, value ? Number(value) : null);
      refreshFiles();
    } catch (err) {
      showError('Could not assign room: ' + getErrorMessage(err));
    }
  }

  async function handleMoveFile(file: ProjectFile, e: Event) {
    const value = (e.target as HTMLSelectElement).value;
    const folderId = value === '' ? null : Number(value);
    try {
      await moveFile(file.id, folderId);
      refreshFiles();
    } catch (err) {
      showError('Could not move file: ' + getErrorMessage(err));
    }
  }

  async function handleCreateFolder() {
    const name = newFolderName.trim();
    if (!name) {
      showNewFolderInput = false;
      return;
    }
    try {
      const folder = await createFolder(projectId, name);
      newFolderName = '';
      showNewFolderInput = false;
      refreshFolders();
      showSuccess('Folder created.');
      navigateToFolder(folder.id);
    } catch (e) {
      showError('Could not create folder: ' + getErrorMessage(e));
    }
  }

  function startRenameFolder(folder: ProjectFileFolder) {
    renamingFolderId = folder.id;
    renameValue = folder.name;
  }

  async function handleRenameFolder(folder: ProjectFileFolder) {
    const name = renameValue.trim();
    renamingFolderId = null;
    if (!name || name === folder.name) return;
    try {
      await renameFolder(folder.id, name);
      refreshFolders();
      showSuccess('Folder renamed.');
    } catch (e) {
      showError('Could not rename folder: ' + getErrorMessage(e));
    }
  }

  function confirmDeleteFolder(folder: ProjectFileFolder) {
    folderToDelete = folder;
    showDeleteFolderConfirm = true;
  }

  async function handleDeleteFolder() {
    showDeleteFolderConfirm = false;
    if (!folderToDelete) return;
    try {
      await deleteFolder(folderToDelete.id);
      showSuccess('Folder deleted. Its files moved to the root.');
      refreshFolders();
      if (activeFolderId === folderToDelete.id) {
        navigateToRoot();
      }
    } catch (e) {
      showError('Delete failed: ' + getErrorMessage(e));
    } finally {
      folderToDelete = null;
    }
  }

  async function handleDownloadZip(folder: ProjectFileFolder) {
    try {
      await downloadFolderZip(projectId, folder.id, folder.name);
    } catch (e) {
      showError('Download failed: ' + getErrorMessage(e));
    }
  }
</script>

{#snippet fileRow(file: ProjectFile)}
  <div class="flex items-center justify-between p-3 bg-bg-rail rounded-lg gap-3">
    <div class="flex items-center space-x-3 min-w-0">
      <FolderOpen class="w-5 h-5 text-text-muted shrink-0" />
      <div class="min-w-0">
        <button
          type="button"
          class="text-text-primary hover:text-text-secondary font-medium text-sm underline cursor-pointer truncate block"
          onclick={() => openFile(file)}
        >
          {file.filename}
        </button>
        <p class="text-xs text-text-muted mt-1">
          {(file.size / 1024).toFixed(1)} KB
        </p>
      </div>
    </div>
    <div class="flex items-center gap-2 shrink-0">
      {#if unitId != null && roomId == null}
        <select
          aria-label="Room for {file.filename}"
          class="max-w-40 text-xs bg-bg-canvas border border-border-default rounded px-1.5 py-1 text-text-secondary"
          value={file.room_id ?? ''}
          onchange={(e) => handleAssignRoom(file, e)}
        >
          <option value="">No room</option>
          {#each roomsQ.data ?? [] as room}
            <option value={room.id}>{room.name}</option>
          {/each}
        </select>
      {/if}
      <select
        class="text-xs bg-bg-canvas border border-border-default rounded px-1.5 py-1 text-text-secondary"
        value={file.folder_id != null ? String(file.folder_id) : ''}
        onchange={(e) => handleMoveFile(file, e)}
        title="Move to folder"
      >
        <option value="">Root</option>
        {#each folders as folder}
          <option value={folder.id}>{folder.name}</option>
        {/each}
      </select>
      <button
        class="px-3 py-1 text-sm font-medium text-state-error hover:text-state-error hover:bg-state-error/10 rounded transition-colors"
        onclick={() => confirmDeleteFile(file)}
      >
        Delete
      </button>
    </div>
  </div>
{/snippet}

<div class="space-y-4">
  {#if filesQ.isError}
    <p role="alert" class="text-sm text-state-error">
      Could not load files: {getErrorMessage(filesQ.error)}
    </p>
  {/if}
  {#if uploadError}
    <div class="bg-state-error/10 border border-state-error/30 text-state-error px-4 py-3 rounded-md text-sm">
      {uploadError}
    </div>
  {/if}

  {#if activeFolder != null}
    <!-- ── Folder view ── -->
    <div class="flex items-center gap-3">
      <button
        type="button"
        class="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
        onclick={navigateToRoot}
      >
        <ArrowLeft class="w-4 h-4" />
        Back to root
      </button>
    </div>

    <div class="flex items-center justify-between gap-2">
      {#if renamingFolderId === activeFolder.id}
        <input
          type="text"
          class="flex-1 text-sm bg-bg-canvas border border-border-default rounded px-2 py-1 text-text-primary"
          bind:value={renameValue}
          onkeydown={(e) => {
            if (e.key === 'Enter') handleRenameFolder(activeFolder);
            if (e.key === 'Escape') renamingFolderId = null;
          }}
        />
        <button
          type="button"
          class="p-1.5 text-accent-primary hover:opacity-80"
          title="Save"
          onclick={() => handleRenameFolder(activeFolder)}
        >
          Save
        </button>
        <button
          type="button"
          class="p-1.5 text-text-muted hover:text-text-primary"
          title="Cancel"
          onclick={() => (renamingFolderId = null)}
        >
          <X class="w-4 h-4" />
        </button>
      {:else}
        <div class="flex items-center gap-2 min-w-0">
          <FolderOpen class="w-5 h-5 text-text-muted shrink-0" />
          <h3 class="text-lg font-semibold text-text-primary truncate">{activeFolder.name}</h3>
          <span class="text-sm text-text-muted">({filesInFolder(activeFolder.id).length})</span>
        </div>
        <div class="flex items-center gap-1 shrink-0">
          <button
            type="button"
            class="p-1.5 text-text-muted hover:text-text-primary hover:bg-bg-hover rounded"
            title="Download as zip"
            onclick={() => handleDownloadZip(activeFolder)}
          >
            <Download class="w-4 h-4" />
          </button>
          <button
            type="button"
            class="p-1.5 text-text-muted hover:text-text-primary hover:bg-bg-hover rounded"
            title="Rename folder"
            onclick={() => startRenameFolder(activeFolder)}
          >
            <Pencil class="w-4 h-4" />
          </button>
          <button
            type="button"
            class="px-2 py-1 text-xs font-medium text-state-error hover:bg-state-error/10 rounded"
            onclick={() => confirmDeleteFolder(activeFolder)}
          >
            Delete
          </button>
        </div>
      {/if}
    </div>

    <div
      role="button"
      tabindex="0"
      class="border-2 border-dashed rounded-lg px-4 py-8 text-center cursor-pointer transition-colors {dragging
        ? 'border-accent-primary bg-accent-primary/5'
        : 'border-border-strong hover:border-border-strong hover:bg-bg-hover'}"
      ondragover={handleDragOver}
      ondragleave={handleDragLeave}
      ondrop={handleDrop}
      onclick={() => fileInputEl?.click()}
      onkeydown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          fileInputEl?.click();
        }
      }}
    >
      <UploadCloud class="mx-auto h-8 w-8 text-text-muted mb-2" />
      <p class="text-sm text-text-secondary">
        {uploading ? 'Uploading…' : 'Drop files or folders here, or click to browse'}
      </p>
      {#if roomId == null}
        <p class="text-xs text-text-muted mt-1">
          or
          <button
            type="button"
            class="underline hover:text-text-primary cursor-pointer"
            onclick={handleDirButtonClick}
          >
            upload a folder
          </button>
        </p>
      {/if}
    </div>

    {#if filesInFolder(activeFolder.id).length > 0}
      <div class="space-y-2">
        {#each filesInFolder(activeFolder.id) as file}
          {@render fileRow(file)}
        {/each}
      </div>
    {:else}
      <div class="text-center py-8">
        <FolderOpen class="mx-auto h-10 w-10 text-text-muted" />
        <p class="mt-2 text-sm text-text-secondary">This folder is empty</p>
      </div>
    {/if}

  {:else}
    <!-- ── Root view ── -->

    <div
      role="button"
      tabindex="0"
      class="border-2 border-dashed rounded-lg px-4 py-8 text-center cursor-pointer transition-colors {dragging
        ? 'border-accent-primary bg-accent-primary/5'
        : 'border-border-strong hover:border-border-strong hover:bg-bg-hover'}"
      ondragover={handleDragOver}
      ondragleave={handleDragLeave}
      ondrop={handleDrop}
      onclick={() => fileInputEl?.click()}
      onkeydown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          fileInputEl?.click();
        }
      }}
    >
      <UploadCloud class="mx-auto h-8 w-8 text-text-muted mb-2" />
      <p class="text-sm text-text-secondary">
        {uploading ? 'Uploading…' : 'Drag and drop files here, or click to browse'}
      </p>
      {#if roomId == null}
        <p class="text-xs text-text-muted mt-1">
          or
          <button
            type="button"
            class="underline hover:text-text-primary cursor-pointer"
            onclick={handleDirButtonClick}
          >
            upload a folder
          </button>
        </p>
      {/if}
    </div>

    {#if roomId == null}
      <div class="flex items-center justify-between">
        <h4 class="text-xs font-medium text-text-muted uppercase tracking-wide">Folders</h4>
        {#if !showNewFolderInput}
          <button
            type="button"
            class="inline-flex items-center gap-1 text-xs font-medium text-accent-primary hover:opacity-80"
            onclick={() => (showNewFolderInput = true)}
          >
            <FolderPlus class="w-3.5 h-3.5" />
            New folder
          </button>
        {/if}
      </div>

      {#if showNewFolderInput}
        <div class="flex items-center gap-2">
          <input
            type="text"
            class="flex-1 text-sm bg-bg-canvas border border-border-default rounded px-2 py-1 text-text-primary"
            placeholder="Folder name"
            bind:value={newFolderName}
            onkeydown={(e) => {
              if (e.key === 'Enter') handleCreateFolder();
              if (e.key === 'Escape') {
                showNewFolderInput = false;
                newFolderName = '';
              }
            }}
          />
          <button type="button" class="px-2 py-1 text-xs font-medium btn-primary rounded" onclick={handleCreateFolder}>
            Create
          </button>
          <button
            type="button"
            class="px-2 py-1 text-xs text-text-muted hover:text-text-primary"
            onclick={() => {
              showNewFolderInput = false;
              newFolderName = '';
            }}
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      {/if}

      {#if folders.length > 0}
        {#each folders as folder}
          <div
            class="border border-border-default rounded-lg overflow-hidden cursor-pointer hover:bg-bg-hover transition-colors"
            onclick={() => { if (renamingFolderId !== folder.id) navigateToFolder(folder.id); }}
            onkeydown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (renamingFolderId !== folder.id) navigateToFolder(folder.id);
              }
            }}
            role="button"
            tabindex="0"
          >
            <div class="flex items-center justify-between gap-2 p-3">
              {#if renamingFolderId === folder.id}
                <input
                  type="text"
                  class="flex-1 text-sm bg-bg-canvas border border-border-default rounded px-2 py-1 text-text-primary"
                  bind:value={renameValue}
                  onkeydown={(e) => {
                    if (e.key === 'Enter') handleRenameFolder(folder);
                    if (e.key === 'Escape') renamingFolderId = null;
                  }}
                />
                <button
                  type="button"
                  class="p-1.5 text-accent-primary hover:opacity-80"
                  title="Save"
                  onclick={(e) => { e.stopPropagation(); handleRenameFolder(folder); }}
                >
                  Save
                </button>
                <button
                  type="button"
                  class="p-1.5 text-text-muted hover:text-text-primary"
                  title="Cancel"
                  onclick={(e) => { e.stopPropagation(); renamingFolderId = null; }}
                >
                  <X class="w-4 h-4" />
                </button>
              {:else}
                <div class="flex items-center gap-2 min-w-0">
                  <FolderOpen class="w-4 h-5 text-text-muted shrink-0" />
                  <span class="text-sm font-medium text-text-primary truncate">{folder.name}</span>
                  <span class="text-xs text-text-muted">({filesInFolder(folder.id).length})</span>
                </div>
              {/if}
              <div class="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  class="p-1.5 text-text-muted hover:text-text-primary hover:bg-bg-hover rounded"
                  title="Download as zip"
                  onclick={(e) => { e.stopPropagation(); handleDownloadZip(folder); }}
                >
                  <Download class="w-4 h-4" />
                </button>
                <button
                  type="button"
                  class="p-1.5 text-text-muted hover:text-text-primary hover:bg-bg-hover rounded"
                  title="Rename folder"
                  onclick={(e) => { e.stopPropagation(); startRenameFolder(folder); }}
                >
                  <Pencil class="w-4 h-4" />
                </button>
                <button
                  type="button"
                  class="px-2 py-1 text-xs font-medium text-state-error hover:bg-state-error/10 rounded"
                  onclick={(e) => { e.stopPropagation(); confirmDeleteFolder(folder); }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        {/each}
      {:else if !showNewFolderInput}
        <p class="text-sm text-text-muted text-center py-4">No folders yet</p>
      {/if}

    {/if}

    {#if !files || files.length === 0}
      <div class="text-center py-8">
        <FolderOpen class="mx-auto h-10 w-10 text-text-muted" />
        <h3 class="mt-2 text-sm font-medium text-text-primary">{emptyLabel}</h3>
      </div>
    {:else if rootFiles.length > 0}
      <div class="space-y-2">
        {#each rootFiles as file}
          {@render fileRow(file)}
        {/each}
      </div>
    {/if}
  {/if}

  <input
    bind:this={fileInputEl}
    type="file"
    multiple
    class="hidden"
    disabled={uploading}
    onchange={handleFileInputChange}
  />

  <ConfirmDialog
    bind:open={showDeleteConfirm}
    title="Delete File"
    message={`Are you sure you want to delete "${fileToDelete?.filename}"?`}
    confirmText="Delete"
    variant="danger"
    onconfirm={handleDeleteFile}
  />

  <ConfirmDialog
    bind:open={showDeleteFolderConfirm}
    title="Delete Folder"
    message={`Are you sure you want to delete "${folderToDelete?.name}"? Its files will move to the root.`}
    confirmText="Delete"
    variant="danger"
    onconfirm={handleDeleteFolder}
  />
</div>
